<script setup lang="ts">
import { definePageMeta } from '#imports'
import { useUiState } from '~/composables/useUiState'

const { setHideBackButton } = useUiState()
setHideBackButton(true)

definePageMeta({
  name: 'terms of use',
  layout: 'public',
  showSearch: false,
  showNav: false,
  hideCart: true,
  unauthorized: true,
  hideLanguages: true,
  skipMiddleware: true,
})
</script>

<template>
  <section class="mx-auto my-8 max-w-[1280px] px-4">
    <h1 class="mb-6 text-center text-4xl font-bold">TERMS OF USE</h1>
    <p>Last Updated: December 20, 2023</p>
    <p>
      These Terms of Use inform you of the terms, conditions, disclaimers,
      notices, policies, and agreements (collectively, "Terms") that apply when
      you access, use or visit the website offering eyewear furnished by Zenni
      Optical, Inc. to personnel of the company (“Employer”) identified on the
      platform and website where these Terms appear (the "Site"), or the
      services we provide through the Site (the Site and these services
      constituting the "Service").
    </p>
    <p>
      The Service is provided by Zenni Optical, Inc. ("Zenni," "we," "us" and
      "our"), an independent party not affiliated with the Employer in any way
      other than as an independent contractor providing the Service and products
      to eligible personnel of the Employer, as determined by the Employer,
      pursuant to the Employer’s own terms and policies. The Service offers a
      curated selection of eyewear options the Employer makes available to its
      eligible personnel on a subsidized basis on terms communicated separately
      by the Employer to its personnel. Zenni does not control or establish
      those terms.
    </p>
    <p>
      BY AFFIRMATIVELY ACKNOWLEDGING YOUR AGREEMENT TO THESE TERMS OF USE, YOU
      ARE AGREEING TO THESE TERMS OF USE, WHICH CONSTITUTE A BINDING LEGAL
      AGREEMENT BETWEEN YOU AND ZENNI. YOUR CONTINUED ACCESS AND USE OF THE
      SERVICE ARE CONDITIONAL ON YOUR ACCEPTANCE OF AND CONTINUED COMPLIANCE
      WITH THE TERMS OF USE SET OUT BELOW. Please read these Terms and any other
      agreements referenced in this document carefully. If you do not agree to
      be bound by these Terms, you may not use or access the Service.
    </p>
    <p>
      <u>Notice Regarding Dispute Resolution and Your Right to Opt-Out:</u>
      These Terms contain provisions that govern how claims you and we may have
      against each other are resolved (see Section 14 below), including an
      agreement and obligation to arbitrate disputes, which will, subject to
      limited exceptions, require you to submit claims you have against us to
      binding arbitration, unless you opt-out in accordance with Section 14€.
      Unless you opt-out of arbitration: (a) you will only be permitted to
      pursue claims against us on an individual basis, not as part of any class
      or representative action or proceeding and (b) you will only be permitted
      to seek relief (including monetary, injunctive, and declaratory relief) on
      an individual basis. If you are located in the EU or the UK, in most cases
      the Dispute Resolution provisions will not affect your right to resolve
      disputes in your local court or tribunal.
    </p>
    <p>
      Personal information you submit is governed by Zenni’s
      <a href="https://www.zennioptical.com/privacy-policy">privacy policy</a>.
      Without limiting the foregoing, such information also may be shared with
      the Employer to verify purchases and confirm eligibility of purchasers per
      the Employer’s terms and policies.
    </p>
    <p>Click on the links below to jump to specific section of the Terms.</p>
    <p>
      <strong>TABLE OF CONTENTS</strong>
    </p>
    <ol type="1">
      <li>
        <a link="" href="#AffirmativeRepresentations"
          ><u
            >AFFIRMATIVE REPRESENTATIONS REGARDING YOUR USE OF THE SERVICE</u
          ></a
        >
      </li>
      <li>
        <a link="" href="#UserAccountsAndRegistration"
          ><u>USER ACCOUNTS AND REGISTRATION</u></a
        >
        <ol type="a">
          <li>
            <a link="" href="#UserRegistration"><u>User Registration.</u></a>
          </li>
          <li>
            <a link="" href="#UserAccounts"><u>User Accounts.</u></a>
          </li>
        </ol>
      </li>
    </ol>
    <ol start="3" type="1">
      <li>
        <a link="" href="#ProhibitedUses"><u>PROHIBITED USES</u></a>
      </li>
      <li>
        <a link="" href="#OurIntellectual"
          ><u>OUR INTELLECTUAL PROPERTY RIGHTS</u></a
        >
        <ol type="a">
          <li>
            <a link="" href="#AZenniMaterials"><u>Zenni Materials.</u></a>
          </li>
          <li>
            <a link="" href="#BLicenseGrantedbyZenni"
              ><u>License Granted by Zenni.</u></a
            >
          </li>
        </ol>
      </li>
    </ol>
    <ol start="5" type="1">
      <li>
        <a link="" href="#UserGeneratedContent"
          ><u>USER GENERATED CONTENT</u></a
        >
        <ol type="a">
          <li>
            <a link="" href="#UserContent"><u>User Content.</u></a>
          </li>
          <li>
            <a link="" href="#BLicenseGrantedbyYou"
              ><u>License Granted by You.</u></a
            >
          </li>
        </ol>
      </li>
    </ol>
    <ol start="6" type="1">
      <li>
        <a link="" href="#CopyrightInfringementNotices"
          ><u>COPYRIGHT INFRINGEMENT NOTICES</u></a
        >
      </li>
      <li>
        <a link="" href="#TermsofSale"><u>TERMS OF SALE</u></a>
      </li>
      <ol type="a">
        <li>
          <a link="" href="#PlacinganOrderThroughtheService"
            ><u>Placing an Order Through the Service.</u></a
          >
        </li>
        <li>
          <a link="" href="#CustomEngraving"
            ><u>Custom Engraving (not applicable to Fast Frame).</u></a
          >
        </li>
        <li>
          <a link="" href="#DeliveryforStandardShipping"
            ><u>Delivery for Standard Shipping.</u></a
          >
        </li>
        <li>
          <a link="" href="#DFastFrameRushDelivery"
            ><u>Fast Frame Rush Delivery.</u></a
          >
        </li>
        <li>
          <a link="" href="#EReturns"><u>Returns.</u></a>
        </li>
        <li>
          <a link="" href="#FCouponsSpecialSales"
            ><u>Coupons /Special Sales.</u></a
          >
        </li>
        <li>
          <a link="" href="#GRightofWithdrawal"
            ><u>Right of Withdrawal (applicable to EU/UK residents only)</u></a
          >
        </li>
      </ol>
    </ol>
    <ol start="8" type="1">
      <li>
        <a link="" href="#Contest"><u>CONTEST</u><u>S</u></a>
      </li>
      <li>
        <a link="" href="#ThirdPartySites"><u>THIRD PARTY SITES</u></a>
      </li>
      <li>
        <a link="" href="#SocialMediaGuidelines"
          ><u>SOCIAL MEDIA GUIDELINES</u></a
        >
      </li>
      <li>
        <a link="" href="#OurManagementoftheService"
          ><u>OUR MANAGEMENT OF THE SERVICE</u></a
        >
      </li>
      <ol type="a">
        <li>
          <a link="" href="#AOurRighttoManagetheService"
            ><u>Our Right to Manage the Service.</u></a
          >
        </li>
        <li>
          <a link="" href="#BOurRighttoTerminateUsers"
            ><u>Our Right to Terminate Users.</u></a
          >
        </li>
      </ol>
    </ol>
    <ol start="12" type="1">
      <li>
        <a link="" href="#WarrantyDisclaimer"
          ><u>WARRANTY DISCLAIMER; LIMITATION ON LIABILITY</u></a
        >
      </li>
      <ol type="a">
        <li>
          <a link="" href="#ADisclaimerofWarranties"
            ><u>Disclaimer of Warranties.</u></a
          >
        </li>
        <li>
          <a link="" href="#BLimitedLiability"><u>Limited Liability.</u></a>
        </li>
        <li>
          <a link="" href="#CYourPurchasesareforPersonal"
            ><u
              >Your Purchases Are for Personal and Noncommercial Use Only.</u
            ></a
          >
        </li>
        <li>
          <a link="" href="#DExceptionstoDisclaimersandLia"
            ><u>Exceptions To Disclaimers and Liability Limitations.</u></a
          >
        </li>
      </ol>
    </ol>
    <ol start="13" type="1">
      <li>
        <a link="" href="#Indemnification"><u>INDEMNIFICATION</u></a>
      </li>
      <li>
        <a link="" href="#DisputeResolutionArbitration"
          ><u>DISPUTE RESOLUTION, ARBITRATION AND CLASS ACTION WAIVER</u></a
        >
      </li>
      <ol type="a">
        <li>
          <a link="" href="#AInformalDisputeResolution"
            ><u>Informal Dispute Resolution.</u></a
          >
        </li>
        <li>
          <a link="" href="#BAgreementtoBindingArbitration"
            ><u>Agreement to Binding Arbitration.</u></a
          >
        </li>
        <li>
          <a link="" href="#CClassActionandClassArbitrationWaiver"
            ><u>Class Action and Class Arbitration Waiver.</u></a
          >
        </li>
        <li>
          <a link="" href="#DExceptionstoArbitration"
            ><u>Exceptions to Arbitration.</u></a
          >
        </li>
        <li>
          <a link="" href="#EExceptionMassArbitrationBefore"
            ><u>Exception - Mass Arbitration Before NAM.</u></a
          >
        </li>
        <li>
          <a link="" href="#EThirtyDayRighttoOptOut"
            ><u>30 Day Right to Opt-Out.</u></a
          >
        </li>
        <li>
          <a link="" href="#TermforCauseofAction"
            ><u>Term for Cause of Action.</u></a
          >
        </li>
        <li>
          <a link="" href="#HExclusiveVenueforLitigation"
            ><u
              >Exclusive Venue for Litigation Not Subject to Arbitration.</u
            ></a
          >
        </li>
        <li>
          <a link="" href="#Exception"><u>Exception.</u></a>
        </li>
      </ol>
    </ol>
    <ol start="15" type="1">
      <li>
        <a link="" href="#NoticetoCAandNJResidents"
          ><u>NOTICE TO CALIFORNIA AND NEW JERSEY RESIDENTS.</u></a
        >
      </li>
      <ol type="a">
        <li>
          <a link="" href="#ANewJerseyResidents"
            ><u>New Jersey Residents.</u></a
          >
        </li>
        <li>
          <a link="" href="#BCaliforniaResidents"
            ><u>California Residents.</u></a
          >
        </li>
      </ol>
    </ol>
    <ol start="16">
      <li>
        <a link="" href="#Miscellaneous"><u>MISCELLANEOUS</u></a>
      </li>
      <ol type="a">
        <li>
          <a link="" href="#ANoModificationbyOurEmployees"
            ><u>No Modifications by Our Employees.</u></a
          >
        </li>
        <li>
          <a link="" href="#BIndependentContractors"
            ><u>Independent Contractors.</u></a
          >
        </li>
        <li>
          <a link="" href="#CAssignment"><u>Assignment.</u></a>
        </li>
        <li>
          <a link="" href="#DSeverabilityWaiver"
            ><u>Severability; Waiver.</u></a
          >
        </li>
        <li>
          <a link="" href="#EEntireAgreement"><u>Entire Agreement.</u></a>
        </li>
        <li>
          <a link="" href="#FNoticesandElectronicCommunications"
            ><u>Notices and Electronic Communications.</u></a
          >
        </li>
      </ol>
    </ol>
    <ol start="17">
      <li>
        <a link="" href="#UpdatestoTheseTerms"><u>UPDATES TO THESE TERMS</u></a>
      </li>
      <li>
        <a link="" href="#ContactUs"><u>CONTACT US</u></a>
      </li>
    </ol>
    <ol type="1">
      <li>
        <a id="AffirmativeRepresentations"
          >AFFIRMATIVE REPRESENTATIONS REGARDING YOUR USE OF THE SERVICE</a
        >
      </li>
    </ol>
    <p>
      When you use the Service, you represent that: (a) the information you
      submit is truthful and accurate; (b) your use of the Service and your use
      of services available on the Service do not violate any applicable laws or
      regulations; and (c) you are&nbsp; at least 18 years of age and a "natural
      person" in your country of residence, and are fully able and competent to
      enter into the terms, conditions, obligations, representations and
      responsibilities set for in these Terms, and to abide and comply with
      these Terms.
    </p>
    <ol start="2" type="1">
      <li>
        <a id="UserAccountsAndRegistration">USER ACCOUNTS AND REGISTRATION</a>
      </li>
      <ol type="A">
        <li>
          <a id="UserRegistration"><strong>User Registration</strong>.</a> You
          can visit and browse the Service without becoming a registered user of
          the Service, but you will not be able to place an order, view an
          order, track an order, save your favorites, save your recently viewed
          items, save your prescription information, or save your Zenni Frame
          Fit™ photo, unless you are a registered user of the Service. You must
          meet the eligibility requirements of the Employer to order anything
          from the Site and to be entitled to any benefits the Employer may
          offer, all as determined separately by the Employer and communicated
          separately by the Employer to its personnel. If you sign up to become
          a registered user of the Service, you agree: (i) to provide true,
          accurate, current and complete information about yourself as prompted
          by the Service's registration form (the "Registration Data"); (ii) to
          maintain and promptly update the Registration Data to keep it true,
          accurate, current and complete; and (iii) that you will comply with
          the rules governing prohibited uses in Section 3 and User Generated
          Content in Section 5 below.
        </li>
        <li>
          <a id="UserAccounts"><strong>User Accounts</strong>.</a> In order to
          access some features available on the Services, you will need to
          create an account ("User Account"). To create a User Account, you must
          be at least 18 years old, a "natural person" in your country of
          residence, and you must provide truthful and accurate information
          about yourself. If you become a registered user of the Service, you
          will be required to create a user I.D. that will be associated with
          your User Account. You may only create one user I.D. that will be
          associated with your User Account. You may not: (i) select or use as a
          user I.D. a name of another person with the intent to impersonate that
          person; (ii) use as a user I.D. a name subject to any rights of a
          person other than you without appropriate authorization; or (iii) use
          as a user I.D. a name that is otherwise offensive, vulgar or obscene.
          You agree that you are responsible for all activities under your User
          Account, and for maintaining the confidentiality of your password and
          restricting access to your devices so that others may not access our
          Services in violation of these Terms. You may not use another person"s
          User Account. Each time you use a password or identification, you will
          be deemed to be authorized to access, confirm your agreement on the
          updated Terms of Use from time to time, and use the Services in a
          manner consistent with these Terms. Zenni has no obligation to
          investigate the authorization or source of any such access or use of
          the Services. You will be solely responsible for all access to and use
          of the Services by anyone using the password and user I.D. originally
          assigned to you whether or not such access to and use of the Services
          is actually authorized by you, including, without limitation, all
          communications and transmissions and all obligations (including,
          without limitation, financial obligations) incurred through such
          access or use. You are solely responsible for protecting the security
          and confidentiality of the password and user I.D. assigned to you. You
          agree to immediately notify Zenni of any unauthorized use of your
          password or user I.D. or any other breach or threatened breach of the
          Services" security that you become aware of involving or relating to
          the Services. We reserve the right to take any and all actions we deem
          necessary or reasonable to maintain the security of our Services and
          your User Account, including, without limitation, terminating your
          User Account, changing your password or requesting information to
          authorize transactions on your User Account.
        </li>
      </ol>
    </ol>
    <ol start="3" type="1">
      <li>
        <a id="ProhibitedUses">PROHIBITED USES</a>
      </li>
    </ol>
    <p>
      While using the Services, you are required to comply with all applicable
      statutes, orders, regulations, rules and other laws. You are
      <strong><em>prohibited</em></strong>
      to, or assist any other persons to:
    </p>
    <ul type="disc">
      <li>
        use the Service for any unauthorized purpose including collecting
        usernames and/or email addresses of other users by electronic or other
        means for the purpose of sending unsolicited email or other electronic
        communications, or engaging in unauthorized framing of, or linking to,
        the Service without our express written consent;
      </li>
      <li>
        transmit chain letters, bulk or junk email or interfere with, disrupt,
        or create an undue burden on the Service or the networks or services
        connected to the Service, including without limitation, hacking into the
        Service, or using the system to send unsolicited or commercial emails,
        bulletins, comments or other communications;
      </li>
      <li>
        impersonate any other person or entity, sell or let others use your
        profile or password, provide false or misleading identification or
        address information, or invade the privacy, or violate the personal or
        proprietary right, of any person or entity;
      </li>
      <li>
        post advertisements or solicitations for jobs or employment on the
        Service, or otherwise use the Service to hire any person to perform
        work, including, without limitation, posting advertisements or
        solicitations for modeling jobs or talent or talent scouting positions
        on the Service;
      </li>
      <li>
        post on the Service any franchise, pyramid scheme, "club membership,"
        distributorship or sales representative agency arrangement or other
        business opportunity which requires an up-front or periodic payment,
        pays commissions, or requires recruitment of other members,
        sub-distributors or sub-agents;
      </li>
      <li>
        post or generate User Content or use the Services that is intentionally
        misleading, false, or otherwise inappropriate or with the purpose of
        harming others, regardless of whether the Content or its dissemination
        is unlawful;
      </li>
      <li>
        post or generate User Content (as defined in Section 5) or use the
        Services that has any risk or possibility of exploiting, harming, or
        endangering the health or well-being of children or other minors
        ("Children"), such as images of Children in sexualized costumes, poses,
        or a sexual fetishistic context, or which identifies, directly or
        indirectly, alleged victims of child sexual exploitation, or for the
        purpose of exploiting, harming or attempting to exploit or harm Children
        in any way;
      </li>
      <li>
        post or generate User Content or use the Services which may be
        discriminatory towards a person's or class of people's race, religion,
        color, age, ethnicity, national origin, disability, physical, or mental
        characteristics, sexual orientation, gender expression, gender identity,
        family status, medical or genetic condition, personality
        characteristics, or physical appearance, including through the material
        distortion of the behavior of any such person or class of people in a
        manner that causes or is likely to cause that person or class of people
        physical or psychological harm;
      </li>
      <li>
        post or generate User Content or use the Services in such a way that
        damages the image or rights of Zenni, other users or third parties;
      </li>
      <li>
        send spam or other direct marketing communications or posting,
        transmitting or linking to any unsolicited advertising, promotional
        materials, or any other forms of solicitation or commercial content;
      </li>
      <li>
        post or transmit executable programming of any kind, including viruses,
        spyware, trojan horses, Easter eggs, or any other form of computer
        programming or disabling mechanism;
      </li>
      <li>
        use any robot, spider or other automatic program or device, or manual
        process to monitor, copy, summarize, or otherwise extract information
        from this Services or the Content in whole or in part, including,
        without limitation, creating any frames at any other Services pertaining
        to any portions of this Services;
      </li>
      <li>
        decompile, disassemble, modify, translate, adapt, reverse engineer,
        create derivative works from or sublicense the Service, or any portion
        thereof;
      </li>
      <li>
        circumvent, disable or otherwise interfere with security related
        features of the Service or features that prevent or restrict use or
        copying of any Materials (as defined in Section 10) or enforce
        limitations on use of the Service or the Materials on the Service; or
      </li>
      <li>
        intentionally or unintentionally perform or promote any activity that
        would violate any applicable local, provincial/state, national or
        international law, including, but not limited to, any regulations having
        the force of law while using or accessing the Services.
      </li>
    </ul>
    <ol start="4" type="1">
      <li>
        <a id="OurIntellectual">OUR INTELLECTUAL PROPERTY RIGHTS</a>
      </li>
      <ol type="A">
        <li>
          <a id="AZenniMaterials"><strong>Zenni Materials</strong>. </a>Except
          for User Content supplied by you or other users of the Service, all
          names, logos, text, designs, graphics, trademarks, service marks,
          trade dress, characters, interfaces, code, software, images, sounds,
          videos, photographs and other content appearing in or on the Service
          including the trademarks ZENNI® and ZENNI OPTICAL® and the
          Eye-shaped stripped logo® ("Materials") are owned by, used with
          permission or under license by Zenni and/or its licensors, and are
          subject to copyright and other intellectual property rights under
          United States and foreign laws and international conventions. All
          intellectual property rights associated with the Service, and related
          goodwill, are proprietary to Zenni or our licensors. You do not
          acquire any right, title or interest in any Material by accessing or
          using the Service. Any rights not expressly granted herein are
          reserved and retained by Zenni and its affiliates, parents, and
          subsidiaries.
        </li>
        <li>
          <a id="BLicenseGrantedbyZenni"
            ><strong>License Granted by Zenni</strong>. </a
          >The Service and the Materials are for your information and personal
          use only and not for commercial exploitation. Subject to your
          compliance with these Terms, Zenni offers you a limited,
          non-exclusive, non-transferable, non-sub licensable license to access
          the Services and to access, download, and make personal and
          non-commercial use of Materials available on through the Services. You
          may not reproduce, duplicate, copy, sell, resell, visit, or otherwise
          exploit for any reason any part of the Services for any commercial
          purpose without the express written consent of Zenni. Except as
          otherwise expressly authorized herein or in writing by Zenni, you
          agree not to reproduce, modify, rent, lease, perform, display,
          transmit, loan, sell, distribute, or create derivative works based (in
          whole or in part) on all or any part of the Service or the Materials.
          Zenni reserves the right to refuse Service, terminate accounts, and/or
          cancel orders in its discretion, including, without limitation, if we
          believe that user conduct violates applicable law or is harmful to our
          interests. We reserve all rights in and to the Service and the
          Materials. If you download or print a copy of the Materials for your
          own personal use, you must retain all trademark, copyright and other
          proprietary notices contained in and on the Materials.
        </li>
      </ol>
    </ol>
    <ol start="5" type="1">
      <li>
        <a id="UserGeneratedContent">USER GENERATED CONTENT</a>
      </li>
      <ol type="A">
        <li>
          <a id="UserContent"><strong>User Content. </strong></a>Some of our
          services, including uploading photographs in connection with the Zenni
          Frame Fit™ service, are designed to allow you publish or transmit
          through our Services information or content ("User Content"). You
          retain ownership of all User Content you submit, post, display, or
          otherwise make available via the Services. You agree, represent and
          warrant that you have all rights necessary to submit the User Content.
          You shall not upload, post or otherwise make available on or through
          our Services any User Content protected by copyright, trademark or
          other proprietary rights of any third party without the express
          written permission of the owner of such right(s). You shall be solely
          liable for any damages resulting from any infringement of copyright,
          trademark, proprietary rights, or any other harm resulting from such
          User Content. PLEASE DO NOT SEND US ANY USER CONTENT, IDEAS,
          SUGGESTIONS, OR OTHER USER CONTENT THAT YOU WISH TO KEEP PRIVATE OR
          PROPRIETARY OR FOR WHICH YOU EXPECT TO RECEIVE COMPENSATION. By
          sending any ideas, concepts, know-how, proposals, techniques,
          suggestions or other User Content to us, you agree that: (i) we are
          free to use such User Content for any purpose, (ii) such User Content
          will be deemed not to be confidential or proprietary (iii) we may have
          something similar already under consideration or in development, and
          (iv) you are not entitled to any compensation or reimbursement of any
          kind from us under any circumstances unless otherwise expressly agreed
          in writing by us. Be aware that we have no obligation to keep User
          Content confidential unless explicitly stated.
        </li>
        <li>
          <a id="BLicenseGrantedbyYou"
            ><strong>License Granted by You</strong>. </a
          >By submitting, displaying, posting, publishing or uploading any User
          Content, including information, text, graphics or other materials,
          using the Services, you grant Zenni and its related companies, agents,
          licensees, sublicensees, contractors, successors, legal
          representatives, assigns, and third-party service providers, and their
          respective retail partners, marketing or public relations agencies,
          and other affiliates ("Licensed Parties") a non-exclusive, fully-paid,
          royalty-free, irrevocable, transferable, perpetual, worldwide, fully
          sublicensable and unrestricted right and license to use, modify,
          publicly perform, publicly display, reproduce, create derivative works
          from, distribute, and/or otherwise use such User Content in any manner
          to be determined in the "Licensed Parties" sole discretion, including,
          but not limited to, on webpages and social media pages operated by the
          Licensed Parties, in promotional emails and advertisements, and in any
          and all other marketing, promotional and advertising initiatives, and
          in any media now or hereafter known. The Licensed Parties may use,
          display, reproduce, distribute, transmit, create derivative works
          from, combine with other materials, alter and/or edit your User
          Content in any manner in their sole discretion. You will not receive
          any compensation of any kind for the use of User Content. You also
          grant to other users of the Services a non-exclusive, fully-paid,
          royalty-free, and perpetual license to access your Content during the
          course of their use of the Services as authorized herein. In addition,
          You grant Zenni a non-exclusive license to use any User Content for
          research or academic purposes, where Zenni may redistribute, publish,
          or cause the publication of anonymized copies of User Content in
          research papers or academic papers. This license you grant to Zenni
          lasts for as long as User Content is protected by intellectual
          property rights. Content in violation of these Terms may be removed
          without notice.
        </li>
      </ol>
    </ol>
    <ol start="6" type="1">
      <li>
        <a id="CopyrightInfringementNotices">COPYRIGHT INFRINGEMENT NOTICES</a>
      </li>
    </ol>
    <p>
      It is our policy to expeditiously respond to notices of alleged copyright
      infringement that comply with the United States Digital Millennium
      Copyright Act ("<strong>DMCA</strong>"). If you believe that material
      located on or linked to by us violates your copyright, you are encouraged
      to notify us in accordance with the Digital Millennium Copyright Act. To
      do so, please send an email to
      <a href="mailto:legal@zennioptical.com"><u>legal@zennioptical.com</u></a>
      with the Subject Line "DMCA Notice", and include the following:
    </p>
    <ul type="disc">
      <li>Identify the copyrighted work that you claim has been infringed;</li>
      <li>
        Identify the material or link on our Services that you claim is
        infringing your copyrighted work;
      </li>
      <li>
        Provide your full legal name, company affiliation, mailing address,
        telephone number, and email address; and
      </li>
      <li
        style="
          margin-left: 29.64pt;
          margin-bottom: 6pt;
          text-align: justify;
          line-height: normal;
          padding-left: 6.36pt;
          font-family: serif;
        "
      >
        Include in the body of your notice the following statement, followed by
        your electronic or physical signature: "<em
          ><u
            >I hereby state that the information in this Notice is accurate and,
            under penalty of perjury, that I am the owner, or authorized to act
            on behalf of, the owner, of the copyright or of an exclusive right
            under the copyright that is allegedly infringed.</u
          ></em
        >"
      </li>
    </ul>
    <p>
      We will respond to all such notices, including as required or appropriate
      by removing the infringing material or disabling all links to the
      infringing material. We will terminate our user's access to and use of our
      Services if, under appropriate circumstances, the alleged user is
      determined to be a repeat infringer of the copyrights or other
      intellectual property rights of Zenni or others.
    </p>
    <ol start="7">
      <li>
        <a id="TermsofSale">TERMS OF SALE</a>
      </li>
      <ol type="A">
        <li>
          <a id="PlacinganOrderThroughtheService"
            ><strong>Placing an Order Through the Service</strong>. </a
          >In order to place an order through the Service, you must provide us
          with applicable order information. This order information includes (a)
          your full name and correct and complete address and shipping
          information, (b) your phone number, (c) payment information (credit
          card number, expiry and code numbers) for any items where your
          Employer does not cover the full cost, (d) your frame selection, (e)
          your lens type, tints, and coatings, (f) your pupillary distance (PD)
          and (g) your applicable prescription (Rx) information. IF YOUR
          PRESCRIPTION HAS EXPIRED OR IS MORE THAN TWO YEARS OLD, WE RECOMMEND
          THAT YOU CONFIRM IT WITH YOUR DOCTOR OR EYE CARE PROVIDER PRIOR TO
          SUBMITTING AN ORDER. Your order information is saved in your user
          account for future use. We reserve the right to refuse to print,
          engrave or otherwise use submissions that are illegal, obscene,
          defamatory, threatening, pornographic, harassing, hateful, racially or
          ethnically offensive, encourage conduct that would be considered a
          criminal offense, that could give rise to civil liability, violate any
          law, or that we otherwise deem inappropriate or objectionable in our
          discretion.
        </li>
        <li>
          <a id="CustomEngraving"
            ><strong>Custom Engraving (not applicable to Fast Frame)</strong
            >. </a
          >For any custom engraving order, if applicable, you agree that the
          text or artwork you submit is yours, and that you own or have a
          license to all rights, title, and interest in all of the content that
          you provide in connection with placing a custom order. We reserve the
          right to refuse to print, engrave or otherwise use submissions that
          are illegal, obscene, defamatory, threatening, pornographic,
          harassing, hateful, racially or ethnically offensive, encourage
          conduct that would be considered a criminal offense, that could give
          rise to civil liability, violate any law, or that we otherwise deem
          inappropriate or objectionable in our discretion.
        </li>
        <li>
          <a id="DeliveryforStandardShipping"
            ><strong>Delivery for Standard Shipping</strong></a
          >. Most orders placed for standard delivery in the United States are
          shipped from one of our United States distribution centers, and most
          orders placed for standard delivery outside of the United States are
          shipped from Hong Kong. Orders for standard delivery are usually
          shipped from these locations within 7 to 14 business days from date of
          the order, depending on prescription type and other factors, and, as
          with all medical devices, your order is subject to government
          inspection, shipping delays, strikes and other unforeseeable events.
          Therefore, timing of delivery, receipt of the order by you, and other
          timing expectations might be delayed or impaired, and orders can take
          longer than expected. We do not guarantee a specific delivery date.
        </li>
        <li>
          <a id="DFastFrameRushDelivery"
            ><strong>Fast Frame Rush Delivery</strong>.
          </a>
          Fast Frame orders, if available per your Employer’s policies, if you
          choose to purchase it at your own expense, with rush delivery are
          shipped from one of our United States distribution centers. In the
          event we find a discrepancy with your order, we will attempt to notify
          you to verify. If we are unable to reach you, your order may be
          canceled, and a refund will be issued to the original payment method.
        </li>
        <li>
          <a id="EReturns"><strong>&nbsp;</strong><strong>Returns</strong>. </a
          >If you are not satisfied with eyeglasses you have ordered from us,
          you may return those eyeglasses either of the following two ways
          <a id="_cmntref1"></a
          ><a
            id="_cmntref2"
            link=""
            href="https://www.zennioptical.com/c/return"
            >described in our Returns Policy, which you can view here</a
          >. This includes returns on accessories, manufacturing defects, and
          discounted purchases.&nbsp; If you are located in the EU or the UK,
          this Section does not limit or exclude your warranty rights which you
          may have under the mandatory laws of your jurisdiction.
        </li>
        <li>
          <a id="FCouponsSpecialSales"
            ><strong>Coupons /Special Sales</strong>. </a
          >"Coupons" and "Coupon codes," "Sales," "Free Shipping" and other
          special pricing and deals are as advertised only. Please read the
          specific terms for the applicable offer to understand what
          restrictions or limitations may apply in your jurisdiction. All
          special pricing and deals will begin and end on the date and at the
          time specified with the applicable offer. All valid coupons and coupon
          codes will be provided through the Service or through advertising
          sponsored by us. We do not have an obligation to honor any coupons or
          coupon codes that are posted on third party websites or otherwise
          provided by third parties.
        </li>
        <li>
          <a id="GRightofWithdrawal"
            ><strong>Right of Withdrawal</strong> (applicable to EU/UK residents
            only)
          </a>
          <ol type="i">
            <li>
              If you are located in the EU or the UK, you have the following
              right to withdraw from your order in addition to the right of
              return that is granted to you in our <a id="_cmntref3"></a
              ><a
                id="_cmntref4"
                link=""
                href="https://www.zennioptical.com/c/return"
                >Return Policy</a
              >. Your right to withdraw under this Section 7.G does not apply
              when purchasing any of the following items: (i) sealed items which
              are not suitable for return due to health protection or hygiene
              reasons and which were unsealed after delivery; (ii) items that
              are made to your specifications or that are clearly personalized
              to your preferences.
            </li>
            <li>
              You have the right to withdraw from your order within 14 days
              without giving any reason. The withdrawal period is 14 days from
              the day on which you acquire, or a third party other than the
              carrier and indicated by you acquires, physical possession of the
              ordered items. To exercise your right of withdrawal, you must
              inform us (Zenni Optical, Inc. [ADDRESS, TELEPHONE NO, E-MAIL
              ADDRESS]) of your decision to withdraw from this order by an
              unequivocal statement (e.g. a letter sent by post, fax or e-mail).
              You may use the below model withdrawal form, but this is not
              obligatory. To meet the withdrawal deadline, it is sufficient for
              you to send your communication concerning your exercise of the
              right to withdraw before the withdrawal period has expired.
            </li>
            <li>
              If you withdraw your order, we shall reimburse to you (or your
              Employer as applicable) all payments received, including the costs
              of delivery (with the exception of the supplementary costs
              resulting from your choice of a type of delivery other than the
              least expensive type of standard delivery offered by us), without
              undue delay and in any event not later than 14 days from the day
              on which we are informed about your decision to withdraw from this
              order. We will carry out such reimbursement using the same means
              of payment used for the initial transaction, unless you or your
              Employer, as applicable, expressly agree otherwise; in any event,
              no fees will be charged by us as a result of such reimbursement.
              We may withhold reimbursement until we have received the items
              back or you have supplied evidence of having sent back the items,
              whichever is the earliest.
            </li>
            <li>
              You shall send back the items or hand them over to us [or NAME AND
              ADDRESS OF THIRD PARTY TO WHERE ITEMS SHALL BE RETURNED, IF
              APPLICABLE], without undue delay and in any event not later than
              14 days from the day on which you communicate your withdrawal from
              this order to us. The deadline is met if you send back the items
              before the period of 14 days has expired. You will have to bear
              the direct cost of returning the items. You are only liable for
              any diminished value of the items resulting from the handling
              other than what is necessary to establish the nature,
              characteristics and functioning of the items.
            </li>
            <li>
              Model withdrawal form. If you wish to withdraw from the order,
              please complete and return this form:
            </li>
          </ol>
        </li>
      </ol>
    </ol>
    <p
      style="
        margin-top: 12pt;
        margin-left: 72pt;
        margin-bottom: 6pt;
        text-indent: 36pt;
        text-align: justify;
        line-height: normal;
      "
    >
      To: Zenni Optical, Inc. [ADDRESS, E-MAIL ADDRESS]
    </p>
    <p
      style="
        margin-top: 12pt;
        margin-left: 108pt;
        margin-bottom: 6pt;
        text-align: justify;
        line-height: normal;
      "
    >
      I/we (*________) hereby withdraw from the my/our (*______) order for the
      purchase of the following items (*___________ ).
    </p>
    <p
      style="
        margin-top: 12pt;
        margin-left: 108pt;
        margin-bottom: 6pt;
        text-indent: 36pt;
        text-align: justify;
        line-height: normal;
      "
    >
      Name of consumer(s): __________________________
    </p>
    <p
      style="
        margin-top: 12pt;
        margin-left: 108pt;
        margin-bottom: 6pt;
        text-indent: 36pt;
        text-align: justify;
        line-height: normal;
      "
    >
      Address of consumer(s): ________________________
    </p>
    <p
      style="
        margin-top: 12pt;
        margin-left: 108pt;
        margin-bottom: 6pt;
        text-indent: 36pt;
        text-align: justify;
        line-height: normal;
      "
    >
      Signature of consumer(s) (only if this form is notified on paper)
    </p>
    <p
      style="
        margin-top: 12pt;
        margin-left: 108pt;
        margin-bottom: 6pt;
        text-indent: 36pt;
        text-align: justify;
        line-height: normal;
      "
    >
      Date: ________________________
    </p>
    <ol start="8" type="1">
      <li>
        <a id="Contest">CONTEST</a>
      </li>
    </ol>
    <p>
      We may occasionally sponsor contests in connection with the Service. All
      entries submitted for participation in any contest, whether or not
      sponsored solely by us or together with a co-sponsor, shall be deemed a
      User Content (as defined in Section 5(A)) and will be subject to the terms
      set forth in Section 5. Your contest entries and submissions will not be
      returned to you. The rules applicable to each contest will be provided to
      you along with the description of the applicable contest, and the
      applicable rules will supplement the terms and conditions included in
      these Terms. Please read the specific rules for the applicable contest to
      understand what restrictions or limitations may apply in your
      jurisdiction.
    </p>
    <ol start="9" type="1">
      <li>
        <a id="ThirdPartySites">THIRD PARTY SITES. </a>
      </li>
    </ol>
    <p>
      The Service may contain links to websites operated by third parties
      ("<strong>Third-Party Sites</strong>"), including but not limited to the
      Employer. We do not own or operate the Third-Party Sites, and we have not
      reviewed, and cannot review, all of the material, including goods or
      services, made available through Third Party Sites. The availability of
      these links on the Service does not represent, warrant or imply that we
      endorse any Third-Party Sites or any materials, opinions, goods or
      services available on them. Information you submit at a third-party
      resource is subject to the terms of that platform's privacy policy and
      terms, and Zenni has no control over how your information is collected,
      used, or otherwise handled. You agree that your use of third-party
      websites, applications, sites, platforms, and resources, including,
      without limitation, your use of any content, information, data,
      advertising, products, or other materials on or available through such
      third parties, is at your own risk and is subject to the terms and
      conditions of use applicable to such sites and resources. THESE TERMS OF
      USE DO NOT APPLY TO THIRD PARTY SITES. BEFORE VISITING A THIRD-PARTY SITE
      THROUGH LINKS OR OTHER MEANS PROVIDED ON OR THROUGH THE SERVICE, YOU
      SHOULD REVIEW THE THIRD-PARTY SITE"™S TERMS AND CONDITIONS AND PRIVACY
      POLICY, AND INFORM YOURSELF OF THE REGULATIONS, POLICIES AND PRACTICES OF
      THESE THIRD-PARTY SITES. BY USING THE SERVICE, YOU EXPRESSLY RELIEVE ZENNI
      FROM ANY AND ALL LIABILITY ARISING FROM YOUR USE OF ANY THIRD-PARTY SITES.
    </p>
    <ol start="10" type="1">
      <li>
        <a id="SocialMediaGuidelines">SOCIAL MEDIA GUIDELINES</a>
      </li>
    </ol>
    <p>
      If you access or use any of our social media pages on Facebook, Pinterest,
      Twitter, Instagram or YouTube, we ask you to follow the following
      guidelines when contributing content or comments to these social media
      pages:
    </p>
    <ul type="disc">
      <li>
        Be Polite and Courteous. Excessive name calling, profanity, fighting
        words, discriminatory epithets, sexual harassment, bullying, gruesome
        language or the like, will not be tolerated.
      </li>
      <li>
        Stay on Topic. Keep the conversation relevant to the community and
        contribute to the dialogue. We reserve the right to remove content that
        is off-topic, out of context, spam, promotional or links to third party
        sites.
      </li>
      <li>
        Keep It Real. All l postings should come from a real person. We will
        delete any postings from our social media pages that we believe have
        come from fake or anonymous profiles.
      </li>
      <li>
        Contact Us with Your Concerns. We would like to hear about your
        complaints or concerns regarding the Service before you share them on
        our social media pages so that we can help resolve them for you. If you
        are a customer and have a customer service comment, complaint, concern
        or idea, we encourage you to email our customer service team at
        <a href="mailto:service@zennioptical.com"
          ><u>service@zennioptical.com</u></a
        >.
      </li>
    </ul>
    <p>
      We reserve the right to remove content you post on our social media pages
      that violates these social media guidelines.
    </p>
    <ol start="11" type="1">
      <li>
        <a id="OurManagementoftheService">OUR MANAGEMENT OF THE SERVICE</a>
      </li>
      <ol type="A">
        <li>
          <a id="AOurRighttoManagetheService"
            ><strong>Our Right to Manage the Service</strong>. </a
          >We reserve the right, but do not undertake the obligation to: (a)
          monitor or review the Service for violations of these Terms and for
          compliance with our policies; (b) report to law enforcement
          authorities and/or take legal action against anyone who violates these
          Terms; (c) manage the Service in a manner designed to protect our and
          third parties' rights and property or to facilitate the proper
          functioning of the Service; (d) screen our users or members, or
          attempt to verify the statements of our users or members; (e) monitor
          disputes between you and other users or to terminate or block you and
          other users for violating these Terms; and/or (f) refuse, restrict
          access to or the availability of, or remove, delete, edit or disable
          (to the extent technologically feasible) any User Content or any
          portion thereof.
        </li>
        <li>
          <a id="BOurRighttoTerminateUsers"
            ><strong>Our Right to Terminate Users</strong>. </a
          >WITHOUT LIMITING ANY OTHER PROVISION OF THESE TERMS OF USE, WE
          RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION, AND WITHOUT NOTICE OR
          LIABILITY, DENY ACCESS TO AND USE OF THE SERVICE TO ANY PERSON FOR ANY
          REASON OR FOR NO REASON AT ALL, INCLUDING WITHOUT LIMITATION FOR
          BREACH OF ANY REPRESENTATION, WARRANTY OR COVENANT CONTAINED IN THESE
          TERMS OF USE, OR OF ANY APPLICABLE LAW OR REGULATION.
        </li>
      </ol>
    </ol>
    <ol start="12" type="1">
      <li>
        <a id="WarrantyDisclaimer"
          >WARRANTY DISCLAIMER; LIMITATION ON LIABILITY</a
        >
        <ol type="A">
          <li>
            <a id="ADisclaimerofWarranties">DISCLAIMER OF WARRANTIES.</a>
          </li>
          <ol type="i">
            <li>
              TO THE EXTENT PERMITTED BY APPLICABLE LAW, ZENNI HEREBY DISCLAIMS
              ALL WARRANTIES. THE SERVICE AND ALL CONTENT, MATERIALS OR ITEMS
              PROVIDED THROUGH THE SERVICE ARE PROVIDED "AS IS" AND "AS
              AVAILABLE" WITHOUT WARRANTY OF ANY KIND. YOU ASSUME THE RISK OF
              ANY AND ALL DAMAGE OR LOSS FROM USE OF, OR INABILITY TO USE, THE
              SITE, PRODUCTS WE SELL OR THE SERVICE. TO THE MAXIMUM EXTENT
              PERMITTED BY LAW, WE EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES,
              EXPRESS OR IMPLIED, REGARDING THE SITE, OR OUR PRODUCTS OR
              SERVICES INCLUDING, BUT NOT LIMITED TO, ANY IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
              NONINFRINGEMENT.
            </li>
            <li>
              TO THE EXTENT PERMITTED BY APPLICABLE LAW, WE MAKE NO WARRANTIES
              OR REPRESENTATIONS ABOUT THE ACCURACY, RELIABILITY, TIMELINESS OR
              COMPLETENESS OF THE SERVICE"S CONTENT, THE CONTENT OF ANY SITE
              LINKED TO THE SERVICE, USER CONTENT, INFORMATION OR ANY OTHER
              ITEMS OR MATERIALS ON THE SERVICE OR LINKED TO BY THE SERVICE. WE
              ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (A) ERRORS, MISTAKES
              OR INACCURACIES OF CONTENT AND MATERIALS, (B) PERSONAL INJURY OR
              PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR
              ACCESS TO AND USE OF THE SERVICE, (C) ANY UNAUTHORIZED ACCESS TO
              OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL
              INFORMATION STORED THEREIN, (D) ANY INTERRUPTION OR CESSATION OF
              TRANSMISSION TO OR FROM THE SERVICE, (E) ANY BUGS, VIRUSES, TROJAN
              HORSES, OR THE LIKE, WHICH MAY BE TRANSMITTED TO OR THROUGH THE
              SERVICE BY ANY THIRD PARTY, AND/OR (F) ANY ERRORS OR OMISSIONS IN
              ANY CONTENT AND MATERIALS OR FOR ANY LOSS OR DAMAGE OF ANY KIND
              INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED,
              TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE SERVICE.
            </li>
            <li>
              YOU AGREE THAT ZENNI SHALL NOT BE RESPONSIBLE FOR ANY DELAY,
              FAILURE TO DELIVER, FAILURE IN PERFORMANCE OR INTERRUPTION OF
              SERVICE, RESULTING DIRECTLY OR INDIRECTLY: (A) FROM ACTS OF GOD,
              ACTS OF ANY GOVERNMENTAL AGENCY, NATURAL DISASTERS, ACTS OF WAR,
              INSURRECTION OR TERRORISM, STRIKES OR LOCKOUTS, UNAUTHORIZED
              NETWORK OR COMPUTER INTRUSION, OR INTERNET- OR COMPUTER-RELATED
              VIRUSES, HACKER ATTACKS OR OTHER AGENTS INTRODUCED BY A THIRD
              PARTY, FAILURE OF THE INTERNET AND OTHER CONDITIONS BEYOND OUR
              CONTROL, (B) SOLELY FROM ANY TECHNICAL REQUIREMENT FOR WHICH YOU
              ARE RESPONSIBLE, OR (C) SOLELY FROM YOUR INTENTIONAL ACTS OR
              OMISSIONS.
            </li>
            <li>
              IF YOU ARE A RESIDENT FROM THE EU OR THE UK THE EXCLUSION OF OR
              LIMITATIONS ON IMPLIED WARRANTIES OR THE LIMITATIONS ON THE
              APPLICABLE STATUTORY RIGHTS OF A CONSUMER MAY NOT APPLY TO YOU.
            </li>
          </ol>
          <li>
            <a id="BLimitedWarranty"></a
            ><a id="BLimitedLiability"><strong>LIMITED LIABILITY</strong>. </a
            >TO THE EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL WE BE
            LIABLE TO YOU OR ANY THIRD PARTY FOR ANY INDIRECT, CONSEQUENTIAL,
            INCIDENTAL, SPECIAL OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT
            DAMAGES ARISING FROM YOUR USE OF THE SERVICE, USER CONTENT,
            MATERIALS OR ANY OTHER CONTENT THEREIN. NOTWITHSTANDING ANYTHING TO
            THE CONTRARY CONTAINED IN THESE TERMS OF USE, OUR LIABILITY TO YOU
            IN RESPECT OF ANY LOSS OR DAMAGE SUFFERED BY YOU AND ARISING OUT OF
            OR IN CONNECTION WITH THESE TERMS OF USE, WHETHER IN CONTRACT, TORT
            OR FOR BREACH OF STATUTORY DUTY OR IN ANY OTHER WAY SHALL NOT EXCEED
            $50.
          </li>
          <li>
            <a id="CYourPurchasesareforPersonal"
              >YOUR PURCHASES ARE FOR PERSONAL AND NONCOMMERCIAL USE ONLY. </a
            >NOTWITHSTANDING ANYTHING ELSE TO THE CONTRARY HEREIN, ALL
            WARRANTIES ARE VOID AND ZENNI SHALL HAVE NO LIABILITY WHATSOEVER IF
            YOU RESELL, REBRAND, REDISTRIBUTE, OR ADULTERATE (INCLUDING WITHOUT
            LIMITATION BY PLACING NON-ZENNI LENSES INTO ZENNI FRAMES), OR
            OTHERWISE REDISTRIBUTE ANY PRODUCTS PURCHASED FROM ZENNI.
          </li>
          <li>
            <a id="DExceptionstoDisclaimersandLia"
              >EXCEPTIONS TO DISCLAIMERS AND LIABILITY LIMITATIONS. </a
            >SOME STATES OR JURISDICTIONS DO NOT ALLOW THE LIMITATION OR
            EXCLUSION OF CERTAIN WARRANTIES, OR THE EXCLUSION OR LIMITATION OF
            CERTAIN DAMAGES. IF YOU RESIDE IN ONE OF THESE STATES OR
            JURISDICTIONS, THE LIMITATIONS OR EXCLUSIONS IN SECTIONS 12(A) AND
            12(B) MAY NOT APPLY TO YOU.
          </li>
        </ol>
      </li>
    </ol>
    <ol start="13" type="1">
      <li>
        <a id="Indemnification">INDEMNIFICATION</a>
      </li>
    </ol>
    <p>
      You agree and hereby release, indemnify and defend Zenni and its
      directors, officers, employees, agents or service providers from all
      claims, demands, losses, expenses and costs (including reasonable
      attorneys' fees) arising out of or related to: (a) your use of the
      Services, (b) the User Content you provide to Zenni through the Services;
      (c) your conduct or interactions with other users of the Services; or (d)
      or your breach of any provision of these Terms. We will promptly notify
      you of any such claim, and will provide you (at your expense) with
      reasonable assistance in defending the claim.&nbsp; You will allow us to
      participate in the defense and will not settle any such claim without our
      prior written consent.&nbsp; We reserve the right, at our own expense, to
      assume the exclusive defense of any matter otherwise subject to
      indemnification by you.&nbsp; In that event, you will have no further
      obligation to defend us in that matter.
    </p>
    <ol start="14" type="1">
      <li>
        <a id="DisputeResolutionArbitration"
          >DISPUTE RESOLUTION, ARBITRATION AND CLASS ACTION WAIVER</a
        >
      </li>
    </ol>
    <p style="margin-top: 0pt; margin-bottom: 8pt">
      <strong
        >PLEASE READ THIS SECTION CAREFULLY BECAUSE IT REQUIRES THE PARTIES TO
        ARBITRATE THEIR DISPUTES AND LIMITS THE MANNER IN WHICH YOU CAN SEEK
        RELIEF FROM US. YOU UNDERSTAND THAT, ABSENT THIS MANDATORY PROVISION,
        YOU WOULD HAVE THE RIGHT TO SUE IN COURT AND HAVE A JURY TRIAL. YOU
        FURTHER UNDERSTAND THAT THE RIGHTS TO DISCOVERY AND APPEALS MAY BE MORE
        LIMITED IN ARBITRATION THAN IN COURT.</strong
      ><strong>&nbsp; </strong
      ><strong
        >IF YOU ARE LOCATED IN THE EU OR THE UK, THESE PROVISIONS WILL NOT
        AFFECT YOUR RIGHT TO RESOLVE DISPUTES IN YOUR LOCAL COURT OR
        TRIBUNAL.</strong
      >
    </p>
    <ol type="A">
      <li>
        <a id="AInformalDisputer"></a
        ><a id="AInformalDisputeResolution"
          ><strong>Informal Dispute Resolution</strong>.&nbsp; </a
        >We want to address your concerns without needing a formal legal
        case.&nbsp; Most concerns may be quickly resolved in this manner. For
        any dispute with Zenni, you agree to first contact us at
        <a href="mailto:legal@zennioptical.com"
          ><u>legal@zennioptical.com</u></a
        >
        and attempt to resolve the dispute with us informally.&nbsp;Your notice
        of dispute must be individual to you and must include, as applicable,
        your name, your email address, and your residential address.&nbsp;The
        notice of dispute also must explain the facts of the dispute as you
        understand them and tell us what you want us to do to resolve the
        issue.&nbsp;The parties shall use their best efforts to settle any
        dispute directly through consultation and good faith negotiations, and
        you agree that a notice of dispute containing all of the information
        required above, followed by at least 60 days of good faith negotiation,
        are preconditions to either party initiating a lawsuit or
        arbitration.&nbsp;A notice of dispute will not be valid unless it
        contains all of the information required by this paragraph. If you
        commence an arbitration without having previously provided a valid and
        compliant notice of dispute, you and we agree that the applicable
        arbitration provider (or the arbitrator, if one has been appointed) must
        suspend the arbitration pending compliance with this paragraph.&nbsp;You
        and we authorize the arbitration provider or the arbitrator to decide
        summarily whether the party that commenced an arbitration complied with
        these notice requirements, relying solely on this Agreement and the
        notice of dispute provided (if any). All statutes of limitation shall be
        tolled while the parties engage in the informal dispute resolution
        process required by this paragraph.
      </li>
      <li>
        <a id="BAgreementtoBindingArbitration"
          ><strong>Agreement to Binding Arbitration</strong>. </a
        >In the unlikely event that we do not reach an agreed upon solution
        within sixty (60) days from the date the informal dispute resolution is
        pursued pursuant to the Initial Dispute Resolution section above, then
        unless an Exception applies as stated below, we each agree to resolve
        any claim, dispute, or controversy arising out of or in connection with
        or relating to the Services, the Privacy Policy, or this Agreement,
        including but not limited to its formation, performance, breach or
        alleged breach thereof, and regardless of whether the dispute is based
        in contract, statute, regulation, ordinance, or tort (collectively,
        "Claims"), by binding arbitration.&nbsp;The parties acknowledge that
        this Agreement evidences a transaction involving interstate commerce.
        Any arbitration conducted pursuant to the terms of this Agreement shall
        be governed by the Federal Arbitration Act (9 U.S.C. §§ 1-16). The
        application of the United Nations Convention on Contracts for the
        International Sale of Goods is expressly excluded. <br />Except in the
        event of a Mass Arbitration (as defined below), the parties agree that
        all Claims shall be finally settled by binding arbitration administered
        on a confidential basis by JAMS, in accordance with the JAMS Streamlined
        Arbitration Rules and Procedures as modified by this Agreement, but
        excluding any rules or procedures that permit arbitration on a
        class-wide basis (collectively, the "JAMS Rules"). The JAMS Rules
        governing the arbitration may be accessed at
        <a href="https://www.jamsadr.com/adr-rules-procedures"
          ><u>https://www.jamsadr.com/adr-rules-procedures</u></a
        >.&nbsp; The seat of the arbitration will be decided pursuant to the
        JAMS Rules. You or Zenni may elect to appear at the arbitration in
        person or, if you and we both agree, to conduct it remotely online via
        appropriate videoconferencing technology, in lieu of appearing live.
        <br />
        Each party will have the right to use legal counsel in connection with
        arbitration at its own expense. The parties shall select a single
        neutral arbitrator in accordance with the JAMS Rules. Except as
        expressly stated to the contrary below, the arbitrator, and not any
        federal, state or local court or agency, shall have exclusive authority
        to resolve all arbitrability issues, as well as all issues or Claims
        arising out of or relating to the interpretation, applicability, or
        enforceability of this Agreement, including, but not limited to, any
        claim that all or any part of this Agreement is void or voidable. The
        arbitrator shall be empowered to grant whatever relief would be
        available in a court under law or in equity. The arbitrator's award
        shall be in writing and provide a statement of the essential findings
        and conclusions, shall be binding on the parties and may be entered as a
        judgment in any court of competent jurisdiction. In any arbitration
        before JAMS, the parties adopt and agree to implement the JAMS Optional
        Arbitration Appeal Procedure (as it exists on the effective date of this
        Agreement) with respect to any final award in an arbitration arising out
        of or related to this Agreement.<br />
        If you initiate arbitration, to the extent the filing fee for the
        arbitration exceeds Two Hundred and Fifty U.S. Dollars ($250) and the
        claim is found to be non-frivolous and not in bad faith, we will pay the
        additional cost. If we are required to pay the additional cost of the
        filing fees, you should submit a request for payment of fees to JAMS
        along with your form for initiating the arbitration, and we will make
        arrangements to pay all necessary fees directly to JAMS. We will also be
        responsible for paying all other arbitration costs arising in connection
        with the arbitration, other than costs incurred by you for legal
        counsel, travel and other out-of-pocket costs and expenses not
        constituting fees or amounts payable to JAMS. You will not be required
        to pay fees and costs incurred by us if you do not prevail in
        arbitration, unless the arbitrator determines that the claim was
        frivolous or brought in bad faith, in which case the arbitrator may
        award us our attorney's fees, expert witness fees, arbitration fees
        and/or costs. We will also pay JAMS to reimburse you for any portion of
        the $250 filing fee that is more than what you would otherwise have to
        pay to file suit in a court of law.
      </li>
      <li>
        <a id="CClassActionandClassArbitrationWaiver"
          ><strong>Class Action and Class Arbitration Waiver.</strong></a
        >
        TO THE FULLEST EXTENT ALLOWED BY APPLICABLE LAW, YOU AND WE AGREE THAT
        EACH PARTY MAY BRING DISPUTES AGAINST THE OTHER PARTY ONLY IN AN
        INDIVIDUAL CAPACITY, AND NOT AS A CLASS ACTION, COLLECTIVE ACTION OR
        CLASS ARBITRATION, OR AS A PRIVATE ATTORNEY GENERAL. To the extent
        applicable law does not permit waiver of private attorney general
        claims, but permits them to be arbitrated, then such claims shall be
        resolved in arbitration. You and we each further agree to waive our
        respective right to file a class action or seek relief on a class basis.
        If there is a final determination that applicable law precludes
        enforcement of the waiver contained in this paragraph as to any claim,
        cause of action or requested remedy, then that claim, cause of action or
        requested remedy will be severed and may be brought in a court of
        competent jurisdiction, but the waiver contained in this paragraph shall
        be enforced in arbitration on an individual basis as to all other
        claims, causes of action or requested remedies to the fullest extent
        possible. In the event that a claim, cause of action or requested remedy
        is severed pursuant to this paragraph, then you and we agree that the
        claims, causes of action or requested remedies that are not subject to
        arbitration will be stayed until all arbitrable claims, causes of action
        and requested remedies are resolved by the arbitrator. If a court or
        arbitrator decides that any part of this Agreement to Binding
        Arbitration cannot be enforced as to a particular request for public
        injunctive relief, then that request for public injunctive relief (and
        only that request for public injunctive relief) must be brought in court
        and must be stayed pending arbitration of the arbitrable remedies.
      </li>
      <li>
        <a id="DExceptionstoArbitration"
          ><strong>Exceptions to Arbitration</strong>. </a
        >Notwithstanding the parties' agreement to resolve all disputes through
        arbitration, either party may bring an action in state or federal court
        that only asserts claims for patent infringement or invalidity,
        copyright infringement, piracy, moral rights violations, trademark
        infringement, and/or trade secret misappropriation. Excluded claims are
        subject to the jurisdiction provisions in Section 32(h) below. <br />
        Either party may also seek relief in a small claims court for any
        individual disputes or claims within the scope of that court's
        jurisdiction. If an arbitration is filed, before the arbitrator is
        formally appointed, either party can send written notice to the opposing
        party and the applicable arbitration provider that it wants the case
        decided by a small claims court, after which the arbitration provider
        may close the case.
      </li>
      <li>
        <a id="EExceptionMassArbitrationBefore"
          ><strong>Exception"”Mass Arbitration Before NAM.</strong></a
        >
        Notwithstanding the parties' decision to have arbitrations administered
        by JAMS, in the event 25 or more demands for arbitration are filed
        relating to the same or similar subject matter and sharing common issues
        of law or fact, and counsel for the parties submitting the demands are
        the same or coordinated, you and we agree that the demands will
        constitute a "Mass Arbitration." If a Mass Arbitration is commenced, you
        and we agree that it shall not be governed by the JAMS Rules or
        administered by JAMS. Instead, a Mass Arbitration shall be administered
        by National Arbitration &amp; Mediation ("NAM"), a nationally recognized
        arbitration provider, and governed by the NAM rules in effect when the
        Mass Arbitration is filed as modified by this Agreement, including the
        NAM Mass Filing Supplemental Dispute Resolution Rules, but excluding any
        rules that permit arbitration on a class-wide basis (collectively, the
        "NAM Rules"). The NAM Rules are available at
        <a href="http:/www.namadr.com"><u>www.namadr.com</u></a> or by calling
        1-800-358-2550. Notwithstanding anything to the contrary above, you and
        we agree that if either party fails or refuses to commence the Mass
        Arbitration before NAM, you or we may seek an order from a court of
        competent jurisdiction compelling compliance with this agreement and
        compelling administration of the Mass Arbitration before NAM. Pending
        resolution of any such requests to a court, you and we agree that all
        arbitrations comprising the Mass Arbitration (and any obligation to pay
        arbitration fees) shall be stayed. You and we acknowledge that either
        party's failure to comply with this paragraph would irreparably harm the
        other, and you and we agree that a court may issue an order staying the
        arbitrations (and any obligation to pay arbitration fees) until any
        disagreements over the provisions of this paragraph are resolved by the
        court.
      </li>
      <li>
        <a id="EThirtyDayRighttoOptOut"
          ><strong>30 Day Right to Opt-Out. </strong></a
        >You have the right to opt-out and not be bound by the arbitration and
        class action waiver provisions above by sending written notice of your
        decision to opt-out by emailing us at
        <a href="mailto:arbitrationoptout@zenni.com"
          >arbitrationoptout@zenni.com.</a
        >The notice must be sent within thirty (30) days of your first use of
        the Service following the effective date of this Agreement; otherwise
        you shall be bound to arbitrate disputes in accordance with the terms of
        those sections. If you opt-out of these arbitration provisions, we also
        will not be bound by them.
      </li>
      <li>
        <a id="TermforCauseofAction"
          ><strong>Term for Cause of Action. </strong></a
        >You agree that regardless of any statute or law to the contrary, any
        claim or cause of action arising out of or related to use of the
        Services or this Agreement must be filed within two (2) years after such
        claim or cause of action arose or be forever barred.
      </li>
      <li>
        <a id="HExclusiveVenueforLitigation"
          ><strong
            >Exclusive Venue for Litigation Not Subject to Arbitration.
          </strong></a
        >To the extent that the arbitration provisions set forth above do not
        apply or if you have opted out of arbitration, you and we agree to
        submit to the personal jurisdiction of the state and federal courts
        located in Los Angeles County, California and Salt Lake County, Utah,
        and you and we agree that any litigation shall be filed exclusively in
        state or federal courts located in San Francisco, California (except for
        small claims court actions which may be brought in the county where you
        reside). In the event of litigation not subject to this Agreement to
        Binding Arbitration, you and we agree to waive, to the maximum extent
        permitted by law, any right to bring a class action or any right to a
        jury trial, except where a class action or jury trial waiver is not
        permissible under applicable law.
      </li>
      <li>
        <a id="Exception"><strong>Exception. </strong></a>Arbitration may not
        apply if you are a resident of the European Union and subject to the
        Alternative Dispute Resolution Directive (2013/11/EU) and the Online
        Dispute Resolution Regulation (EU 524/2013) (and any implement
        regulations in each member state of the EU), the agreement to arbitrate
        in these Terms will not apply if not permitted by law.
      </li>
    </ol>
    <ol start="15" type="1">
      <li>
        <a id="NoticetoCAandNJResidents"
          >NOTICE TO CALIFORNIA AND NEW JERSEY RESIDENTS</a
        >
      </li>
      <ol type="A">
        <li>
          <a id="ANewJerseyResidents"><strong>New Jersey Residents. </strong></a
          >Notwithstanding any terms set forth in these Terms, if any of the
          provisions set forth in Sections 12 or 14 are held unenforceable, void
          or inapplicable under New Jersey law, then any such provision shall
          not apply to you but the rest of these Terms shall remain binding on
          you and Zenni. In addition, for New Jersey residents, the limitation
          on liability is inapplicable where attorneys' fees, court costs, or
          other damages are mandated by statute. Notwithstanding any provision
          in these Terms, nothing in these Terms is intended to, nor shall it be
          deemed or construed to, limit any rights available to you under the
          Truth-in-Consumer Contract, Warranty and Notice Act.
        </li>
        <li>
          <a id="BCaliforniaResidents"
            ><strong>California Residents</strong>. </a
          >Under California Civil Code Section 1789.3, users located in
          California are entitled to the following consumer rights notice: If a
          user has a question or complaint regarding the Service, please send an
          email to
          <a href="mailto:legal@zennioptical.com"
            ><u>legal@zennioptical.com</u></a
          >. Users may also contact us by writing to Zenni Optical, 448 Ignacio
          Blvd, #332, Novato, CA 94949. California residents may reach the
          Complaint Assistance Unit of the Division of Consumer Services of the
          California Department of Consumer Affairs by mail at 1625 North Market
          Blvd., Suite N 112, Sacramento, CA 95834, or by telephone at (916)
          445-1254 or (800) 952-5210.
        </li>
      </ol>
    </ol>
    <ol start="16">
      <li>
        <a id="Miscellaneous">MISCELLANEOUS</a>
      </li>
      <ol type="A">
        <li>
          <a id="ANoModificationbyOurEmployees"
            ><strong>No Modifications by Our Employees</strong>. </a
          >If any of our employees offers to modify the terms of these Terms, he
          or she is not acting as an agent for us or speaking on our behalf. You
          may not rely, and should not act in reliance on, any statement or
          communication from our employees or anyone else purporting to act on
          our behalf.
        </li>
        <li>
          <a id="BIndependentContractors"
            ><strong>Independent Contractors</strong>. </a
          >Nothing in these Terms shall be deemed to create an agency,
          partnership, joint venture, employer-employee or franchisor-franchisee
          relationship of any kind between us and any user or Employer.
        </li>
        <li>
          <a id="CAssignment"><strong>Assignment</strong>.</a> We may assign our
          rights under these Terms without your approval. These Terms and any
          rights and licenses granted hereunder, may not be transferred or
          assigned by you.
        </li>
        <li>
          <a id="DSeverabilityWaiver"><strong>Severability; Waiver</strong>.</a>
          If, for whatever reason, a court of competent jurisdiction finds any
          term or condition in these Terms to be unenforceable, all other terms
          and conditions will remain unaffected and in full force and effect. No
          waiver of any breach of any provision of these Terms shall constitute
          a waiver of any prior, concurrent, or subsequent breach of the same or
          any other provisions hereof, and no waiver shall be effective unless
          made in writing and signed by an authorized representative of the
          waiving party.
        </li>
        <li>
          <a id="EEntireAgreement"><strong>Entire Agreement</strong>. </a>These
          Terms reflect the entire agreement between the parties related to the
          subject matter hereof and supersede all prior agreements,
          representations, statements, and understandings of the parties.
        </li>
        <li>
          <a id="FNoticesandElectronicCommunications"
            ><strong>Notices and Electronic Communications</strong>. </a
          >When you send emails or text messages to us, you are communicating
          with us electronically. You consent to receive communications from us
          electronically. We will communicate with you by email, text message,
          push notifications services or by posting notices on our Services. You
          agree that all agreements, notices, disclosures and other
          communications that we provide to you electronically satisfy any legal
          requirement that such communications be in writing.
        </li>
      </ol>
    </ol>
    <ol start="17">
      <li>
        <a id="UpdatestoTheseTerms">UPDATES TO THESE TERMS</a>
      </li>
    </ol>
    <p>
      We reserve the right to modify these Terms, at any time without prior
      notice, at our sole discretion. We will use reasonable efforts to notify
      you of such changes. However, it is your responsibility to check the "Last
      Updated" legend at the top of this page periodically to see when these
      Terms were last revised. When changes are made to these Terms, they will
      become immediately effective when published on this web page unless
      otherwise noted. If we modify these Terms materially, to the extent
      permissible under applicable law, such modification will be effective upon
      the earlier of (i) your first use of the Services with actual notice of
      such change, (ii) 30 days from the posting of such change. We may send you
      notice of the updated Terms at our sole discretion, and the manner of the
      notification may include, for example, email, posted notice on the
      Services, or another reasonable manner. Your continued access and use of
      the Services following any such change constitutes your acceptance to be
      bound by the revised Terms then in effect. You agree that you will review
      these Terms periodically and that you shall be bound by these Terms and
      any modifications to it. Since our Services are evolving over time, we may
      also change or discontinue all or any part of the Services, at any time
      and without notice.
    </p>
    <p>
      These Terms will identify the date of the last update. Disputes arising
      under these Terms will be resolved in accordance with the version of the
      Terms in place at the time the Dispute arose. "Disputes" means any
      dispute, action, controversy, or claim arising out of or relating to any
      aspect of these Terms, whether based on contract, tort, statute, fraud,
      misrepresentation, or any other legal or equitable basis and regardless of
      whether a claim arises during or after the termination of these Terms.
    </p>
    <ol start="18">
      <li>
        <a id="ContactUs">CONTACT US</a>
      </li>
    </ol>
    <p>
      If you have any questions about these Terms, your account or an order you
      have placed through the Service, please email us at
      <a href="mailto:redemption.support@zennioptical.com"
        >redemption.support@zennioptical.com</a
      >.
    </p>
  </section>
</template>

<style scoped>
p {
  @apply mb-6;
}
a {
  @apply text-[#007b8f] hover:underline;
}
ol,
ul {
  display: block;
  list-style-type: revert-layer;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-inline-start: 40px;
}
</style>
